import type { AddOnCatalogEntry, AddOnPurchase } from "@closer/shared";
import { Badge, Button, Card, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

const DURUM_ADI: Record<AddOnPurchase["status"], string> = {
  pending_payment: "Ödeme bekleniyor",
  active: "Aktif",
  canceled: "İptal edildi",
  expired: "Sona erdi",
  refunded: "İade edildi",
  chargeback: "Ters ibraz",
  payment_failed: "Ödeme başarısız",
};

/**
 * EK HAKLAR — katalog ve satın alma yaşam döngüsü (CLOS-201 `[175]`).
 *
 * ### ⚠ Onaysız fiyatlı ek hak SATIN ALINAMAZ
 *
 * KARAR 05'in ek hak karşılığı (`lib/entitlements/addOnCatalog.ts`): gerçek fiyat
 * rakamları ürün sahibi onayından ÖNCE UYDURULMUYOR. `priceApproved: false` olan
 * satırlarda satın alma düğmesi devre dışı ve sebep açıkça yazıyor — sessizce
 * tıklanamaz bir düğme bırakmak kullanıcıyı bir arıza sanmaya iterdi.
 */
export function AddOnsPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;

  return (
    <section className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-fg-heading sm:text-3xl">Ek Haklar</h1>
        <p className="text-sm text-fg-muted">
          Ek AI kredisi, iç kullanıcı koltuğu, sözleşme kapasitesi ve depolama satın alma.
        </p>
      </div>

      <PurchasesCard tenantId={tenantId} />
      <CatalogCard tenantId={tenantId} />
    </section>
  );
}

function CatalogCard({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "addon-catalog"),
    queryFn: async () =>
      (await api<AddOnCatalogEntry[]>("/api/billing/addons/catalog", { tenantId })).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const satinAl = useMutation({
    mutationFn: async (entry: AddOnCatalogEntry) =>
      (
        await api<AddOnPurchase>("/api/billing/addons/purchases", {
          method: "POST",
          tenantId,
          headers: { "idempotency-key": idempotency.uret() },
          body: JSON.stringify({
            addOnCode: entry.code,
            quantity: 1,
            // ⚠ Ödeme yöntemi seçimi henüz Faturalama sayfasından gelmiyor (sağlayıcı KAPALI,
            // BillingPage.tsx'in gerekçesi) — bu yüzden istek şimdilik sunucudan aynı
            // gerekçeyle (PROVIDER_ERROR) reddedilecek. Uç ve akış hazır; kart girişi
            // sağlayıcı entegrasyonunu bekliyor.
            paymentMethodId: crypto.randomUUID(),
            purchaseKey: crypto.randomUUID(),
          }),
        })
      ).data,
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "addon-purchases") });
      toast.success("Satın alma başlatıldı");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Ek hak satın alınamadı.");
    },
  });

  if (sorgu.isPending) {
    return (
      <Card title="Ek hak kataloğu">
        <div role="status" aria-label="Ek hak kataloğu yükleniyor">
          <Skeleton lines={4} />
        </div>
      </Card>
    );
  }

  if (sorgu.error || !sorgu.data) {
    return (
      <Card title="Ek hak kataloğu">
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true, existenceIsSecret: false })}
          onRetry={() => void sorgu.refetch()}
        />
      </Card>
    );
  }

  return (
    <Card title="Ek hak kataloğu">
      <ul className="flex flex-col gap-4">
        {sorgu.data.map((entry) => (
          <li
            key={entry.code}
            className="flex items-center justify-between gap-3 border-b border-border pb-3 last:border-0 last:pb-0"
          >
            <div className="flex flex-col">
              <span className="font-medium text-fg-strong">{entry.name}</span>
              <span className="text-sm text-fg-muted">{entry.description}</span>
              <span className="text-xs text-fg-soft">
                {entry.billingMode === "recurring" ? "Dönemsel yenilenir" : "Tek seferlik"}
              </span>
            </div>
            <div className="flex flex-col items-end gap-1">
              {entry.priceApproved && entry.unitPriceMinorUnits !== null ? (
                <span className="text-sm font-medium tabular-nums text-fg-strong">
                  {(entry.unitPriceMinorUnits / 100).toLocaleString("tr-TR", {
                    style: "currency",
                    currency: entry.currency,
                  })}
                </span>
              ) : (
                <Badge tone="neutral">Fiyat henüz onaylı değil</Badge>
              )}
              <Button
                variant="secondary"
                disabled={!entry.priceApproved || satinAl.isPending}
                onClick={() => satinAl.mutate(entry)}
              >
                Satın al
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PurchasesCard({ tenantId }: { tenantId: string }) {
  const { preferences } = usePreferences();
  const toast = useToast();
  const queryClient = useQueryClient();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "addon-purchases"),
    queryFn: async () =>
      (await api<AddOnPurchase[]>("/api/billing/addons/purchases", { tenantId })).data,
  });

  /**
   * ⚠ AD KATALOGDAN GELİYOR, satırdan DEĞİL: satın alma kaydı yalnız `addOnCode` taşıyor
   * (`internal_seat`) ve o bir veritabanı anahtarı — kullanıcının sözlüğünde karşılığı yok
   * (kriter 20). Aynı `queryKey` katalog kartıyla paylaşıldığı için ikinci bir istek
   * gitmiyor; React Query aynı veriyi veriyor.
   *
   * ⚠ Katalog düşerse satır KAYBOLMUYOR, ham koda düşüyor: satın almanın kendisi ikinci bir
   * sorgunun sağlığına bağlanamaz — kullanıcı parasını ödediği hakkı her hâlükârda görmeli.
   */
  const katalog = useQuery({
    queryKey: tenantKey(tenantId, "addon-catalog"),
    queryFn: async () =>
      (await api<AddOnCatalogEntry[]>("/api/billing/addons/catalog", { tenantId })).data,
  });

  const adlar = new Map((katalog.data ?? []).map((e) => [e.code, e.name]));

  const iptalEt = useMutation({
    mutationFn: async (id: string) =>
      (
        await api<AddOnPurchase>(`/api/billing/addons/purchases/${id}/cancel`, {
          method: "POST",
          tenantId,
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "addon-purchases") });
      toast.success("Yenileme kapatıldı");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "İptal edilemedi.");
    },
  });

  if (sorgu.isPending) {
    return (
      <Card title="Satın alımlarım">
        <div role="status" aria-label="Satın alımlar yükleniyor">
          <Skeleton lines={2} />
        </div>
      </Card>
    );
  }

  /**
   * ⚠ ÖNCE HATA, SONRA BOŞLUK — sıra kararın kendisi (CLOS-965).
   *
   * Bu kart eskiden doğrudan `!sorgu.data` diye soruyordu. Sorgu DÜŞTÜĞÜNDE de `data`
   * `undefined` olur; yani sunucu 500 dönerken ekranda *"Henüz satın alınmış bir ek hak
   * yok."* yazıyordu. Kullanıcı için bu "demek ki hiç almamışım" demek — parası çekilmiş
   * bir ek hak, bir arıza yüzünden yok gibi görünüyordu. Aynı ekranın katalog kartı hata
   * dalını zaten okuyordu; asimetri buradaydı.
   *
   * Kural: veri yokluğunu ancak SORGU BAŞARILIYKEN iddia edebiliriz.
   */
  if (sorgu.error) {
    return (
      <Card title="Satın alımlarım">
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true, existenceIsSecret: false })}
          onRetry={() => void sorgu.refetch()}
        />
      </Card>
    );
  }

  if (!sorgu.data || sorgu.data.length === 0) {
    return (
      <Card title="Satın alımlarım">
        <p data-testid="ek-hak-yok" className="text-sm text-fg-muted">
          Henüz satın alınmış bir ek hak yok.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Satın alımlarım">
      <ul className="flex flex-col gap-3">
        {sorgu.data.map((p) => (
          <li
            key={p.id}
            data-testid="ek-hak-satiri"
            className="flex items-center justify-between gap-3 text-sm"
          >
            <div className="flex flex-col">
              <span className="font-medium text-fg-strong">
                {adlar.get(p.addOnCode) ?? p.addOnCode} × {p.quantity}
              </span>
              <span className="text-fg-muted">
                {DURUM_ADI[p.status]}
                {p.periodEnd
                  ? ` · ${formatDate(p.periodEnd, preferences.locale)} tarihine kadar`
                  : ""}
              </span>
            </div>
            {p.status === "active" && p.autoRenew ? (
              <Button
                variant="secondary"
                onClick={() => iptalEt.mutate(p.id)}
                disabled={iptalEt.isPending}
              >
                Yenilemeyi kapat
              </Button>
            ) : (
              <Badge tone="neutral">{DURUM_ADI[p.status]}</Badge>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
